import React, { useState, useEffect, useCallback, memo } from 'react'
import { Box, Button, Stack, Tooltip, IconButton } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { ReactComponent as LeftIcon } from '../../../common/icons/left_icon.svg'
import { ReactComponent as RightIcon } from '../../../common/icons/right_icon.svg'
import { ReactComponent as CenterIcon } from '../../../common/icons/center_icon.svg'

import DeleteIcon from '../../../common/icons/deleteIcon'
import { ReactComponent as CopyIcon } from '../../../common/icons/copy_icon.svg'
import { useField, useForm } from 'react-final-form'
import Module from './Module'
import ThemeSelector from './ThemeSelector'
import { getModuleTheme } from '../../../common/constants/themeConstants'

const MemoizedModule = memo(Module)

const ModulesManager = ({ theme, onModulesChange, handleThemeChange }) => {
  const [activeSequenceNumber, setActiveSequenceNumber] = useState(null)
  const form = useForm()

  const stepsField = useField('steps')
  const steps = stepsField.input.value || []

  const sortedSteps = [...steps].sort((a, b) => a.sequenceNumber - b.sequenceNumber)

  useEffect(() => {
    if (onModulesChange && steps) {
      onModulesChange(steps)
    }
  }, [steps, onModulesChange])

  const createNewModule = useCallback((sequence = 1) => {
    return {
      id: `module-${Date.now()}`,
      type: null,
      sequenceNumber: sequence,
      alignment: 'LEFT'
    }
  }, [])

  useEffect(() => {
    if (steps.length === 0) {
      try {
        const newModule = createNewModule(1)
        form.batch(() => {
          form.change('steps', [newModule])
        })
        setActiveSequenceNumber(1)
      } catch (error) {
        console.error('Error creating initial module:', error)
      }
    }
  }, [form, steps.length, createNewModule])

  const recalculateSequenceNumbers = useCallback(stepsArray => {
    return stepsArray.map((step, index) => ({
      ...step,
      sequenceNumber: index + 1
    }))
  }, [])

  const handleAddModule = useCallback(() => {
    const maxSequence = steps.length > 0 ? Math.max(...steps.map(s => s.sequenceNumber || 0)) : 0

    const newModule = createNewModule(maxSequence + 1)
    const newSteps = [...steps, newModule]
    form.change('steps', newSteps)

    setActiveSequenceNumber(newModule.sequenceNumber)
  }, [steps, form, createNewModule])

  const handleCopyModule = useCallback(() => {
    if (activeSequenceNumber === null) return

    const activeStep = steps.find(m => m.sequenceNumber === activeSequenceNumber)
    if (!activeStep) return

    const maxSequence = Math.max(...steps.map(s => s.sequenceNumber || 0))

    const newModule = {
      ...activeStep,
      id: `module-${Date.now()}`,
      sequenceNumber: maxSequence + 1
    }

    const newSteps = [...steps, newModule]
    form.change('steps', newSteps)
    setActiveSequenceNumber(newModule.sequenceNumber)
  }, [activeSequenceNumber, steps, form])

  const handleDeleteModule = useCallback(
    sequenceNumber => {
      try {
        const filteredSteps = steps.filter(s => s.sequenceNumber !== sequenceNumber)

        if (filteredSteps.length === 0) {
          const newModule = createNewModule(1)
          form.batch(() => {
            form.change('steps', [newModule])
          })
          setActiveSequenceNumber(1)
          return
        }

        const updatedSteps = recalculateSequenceNumbers(filteredSteps)

        if (activeSequenceNumber === sequenceNumber) {
          const nearestSequence = findNearestSequence(updatedSteps, sequenceNumber)
          setActiveSequenceNumber(nearestSequence)
        } else if (activeSequenceNumber > sequenceNumber) {
          setActiveSequenceNumber(activeSequenceNumber - 1)
        }

        form.batch(() => {
          form.change('steps', updatedSteps)
        })
      } catch (error) {
        console.error('Error deleting module:', error)
      }
    },
    [activeSequenceNumber, steps, form, recalculateSequenceNumbers, createNewModule]
  )

  const findNearestSequence = useCallback((stepsArray, deletedSequence) => {
    if (stepsArray.length === 0) return null

    if (deletedSequence === stepsArray.length + 1) {
      return stepsArray.length
    }

    return Math.min(deletedSequence, stepsArray.length)
  }, [])

  const handleModuleSelect = useCallback(sequenceNumber => {
    setActiveSequenceNumber(sequenceNumber)
  }, [])

  const handleModuleChange = useCallback(
    updatedModule => {
      try {
        const moduleIndex = steps.findIndex(s => s.sequenceNumber === updatedModule.sequenceNumber)
        if (moduleIndex === -1) return

        const newSteps = [...steps]
        newSteps[moduleIndex] = updatedModule

        form.batch(() => {
          form.change('steps', newSteps)
        })
      } catch (error) {
        console.error('Error updating module:', error)
      }
    },
    [steps, form]
  )

  const handleAlignmentChange = useCallback(
    alignment => {
      if (activeSequenceNumber === null) return

      const activeStep = steps.find(m => m.sequenceNumber === activeSequenceNumber)
      if (!activeStep) return

      const updatedModule = {
        ...activeStep,
        alignment
      }

      handleModuleChange(updatedModule)
    },
    [activeSequenceNumber, steps, handleModuleChange]
  )

  useEffect(() => {
    if (sortedSteps.length > 0 && activeSequenceNumber === null) {
      setActiveSequenceNumber(sortedSteps[0].sequenceNumber)
    } else if (sortedSteps.length === 0) {
      setActiveSequenceNumber(null)
    } else if (activeSequenceNumber !== null && !sortedSteps.some(s => s.sequenceNumber === activeSequenceNumber)) {
      setActiveSequenceNumber(sortedSteps[0].sequenceNumber)
    }
  }, [sortedSteps, activeSequenceNumber])

  useEffect(() => {
    if (steps.length > 0 && steps.some(step => step.sequenceNumber === undefined)) {
      const stepsWithSequence = recalculateSequenceNumbers(steps)
      form.change('steps', stepsWithSequence)
    }
  }, [steps, form, recalculateSequenceNumbers])

  const activeStep = sortedSteps.find(s => s.sequenceNumber === activeSequenceNumber)
  const canAddModule = activeStep && activeStep.type !== null

  return (
    <Box sx={styles.container}>
      <Box sx={styles.modulesContainer}>
        {sortedSteps.map(step => {
          const activeIndex = sortedSteps.findIndex(s => s.sequenceNumber === activeSequenceNumber)
          const currentIndex = sortedSteps.findIndex(s => s.sequenceNumber === step.sequenceNumber)
          const distance = currentIndex - activeIndex
          const moduleIndex = (step.sequenceNumber || 1) - 1
          const moduleTheme = getModuleTheme(theme, moduleIndex)

          return (
            <MemoizedModule
              key={`module-${step.id || step.sequenceNumber}`}
              step={step}
              theme={moduleTheme}
              name={`steps[${steps.indexOf(step)}]`}
              isActive={step.sequenceNumber === activeSequenceNumber}
              style={{
                ...((distance > 2 || distance < -2) && { display: 'none' }),
                opacity: distance == 2 || distance == -2 ? 0.5 : 1,
                boxShadow: ' -5px 5px 10px 0px rgba(0, 0, 0, 0.2)',

                transform: `translateX(${-distance * 25}px)
             translateY(${distance * 4}px)
             scale(${step.sequenceNumber === activeSequenceNumber ? 1 : 1 - 0.03 * Math.abs(distance)})`,
                zIndex: sortedSteps.length - Math.abs(distance),
                transition: 'transform 0.3s ease, z-index 0.3s ease'
              }}
              onClick={() => handleModuleSelect(step.sequenceNumber)}
            />
          )
        })}
      </Box>
      <Box sx={styles.moduleActions}>
        <ThemeSelector onThemeChange={handleThemeChange} selectedTheme={theme} />

        <Box sx={styles.alignContainer}>
          <Tooltip title='Align left'>
            <span>
              <IconButton
                onClick={() => handleAlignmentChange('LEFT')}
                sx={{
                  backgroundColor:
                    activeStep?.alignment === 'LEFT' || activeStep?.alignment === null ? '#0246F8' : '#fff',
                  ...styles.alignButton
                }}
              >
                <LeftIcon
                  style={{
                    stroke: activeStep?.alignment === 'LEFT' || activeStep?.alignment === null ? '#fff' : '#1B1B26'
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title='Align center'>
            <span>
              <IconButton
                onClick={() => handleAlignmentChange('CENTER')}
                sx={{
                  backgroundColor: activeStep?.alignment === 'CENTER' ? '#0246F8' : '#fff',
                  ...styles.alignButton
                }}
                disabled={sortedSteps.length === 0 || !canAddModule}
              >
                <CenterIcon
                  style={{
                    stroke: activeStep?.alignment === 'CENTER' ? '#fff' : '#1B1B26'
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title='Align right'>
            <span>
              <IconButton
                onClick={() => handleAlignmentChange('RIGHT')}
                sx={{
                  backgroundColor: activeStep?.alignment === 'RIGHT' ? '#0246F8' : '#fff',
                  ...styles.alignButton
                }}
                disabled={sortedSteps.length === 0 || !canAddModule}
              >
                <RightIcon
                  style={{
                    stroke: activeStep?.alignment === 'RIGHT' ? '#fff' : '#1B1B26'
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Tooltip title='Copy current card'>
          <span>
            <IconButton
              onClick={handleCopyModule}
              sx={styles.actionButton}
              disabled={sortedSteps.length === 0 || !canAddModule}
            >
              <CopyIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title='Delete current card'>
          <span>
            <IconButton
              onClick={() => handleDeleteModule(activeSequenceNumber)}
              sx={styles.deleteButton}
              disabled={sortedSteps.length === 0 || !canAddModule}
            >
              <DeleteIcon color={'#76767D'} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Stack direction='row' spacing={2} sx={styles.controls}>
        <Box sx={styles.paginationContainer}>
          {sortedSteps.map(step => (
            <Tooltip
              key={`pagination-${step.id || step.sequenceNumber}`}
              title={`Navigate to Card ${step.sequenceNumber}`}
            >
              <Button
                variant={step.sequenceNumber === activeSequenceNumber ? 'contained' : 'text'}
                onClick={() => handleModuleSelect(step.sequenceNumber)}
                sx={styles.paginationButton}
              >
                {step.sequenceNumber}
              </Button>
            </Tooltip>
          ))}
          <Tooltip title={canAddModule ? 'Add new card' : 'Select card type first'}>
            <span>
              <IconButton onClick={handleAddModule} sx={styles.addButton} disabled={!canAddModule}>
                <AddIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Stack>
    </Box>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    gap: '12px'
  },
  alignButton: {
    padding: '0'
  },
  alignContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '8px',
    border: '1px solid #76767D',
    borderRadius: '20px',
    backgroundColor: '#fff',
    padding: '4px'
  },
  modulesContainer: {
    position: 'relative',
    flex: 1,
    minHeight: '450px',
    padding: '40px 100px 24px 24px',
    borderRadius: '16px',
    overflow: 'visible',
    marginBottom: '10px'
  },
  controls: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 'auto'
  },
  moduleActions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: '8px'
  },
  actionButton: {
    border: '1px solid rgba(118, 118, 125, 1)',
    height: 36,
    width: 36,
    backgroundColor: '#fff',
    '&.Mui-disabled': { opacity: 0.5, border: '1px solid rgba(118, 118, 125, 0.5)', backgroundColor: '#f5f5f5' },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    }
  },
  deleteButton: {
    border: '1px solid rgba(118, 118, 125, 1)',
    height: 36,
    width: 36,
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: 'rgba(255, 82, 82, 0.08)' },
    '&.Mui-disabled': { opacity: 0.5, border: '1px solid rgba(118, 118, 125, 0.5)', backgroundColor: '#f5f5f5' }
  },
  paginationContainer: {
    display: 'flex',
    gap: '8px'
  },
  paginationButton: {
    color: 'white',
    minWidth: '40px',
    height: '40px',
    padding: '0',
    borderRadius: '20px',
    '&.MuiButton-contained': {
      backgroundColor: '#6123FF',
      color: 'white'
    },
    '&.MuiButton-text': {
      backgroundColor: 'none',
      color: '#76767D'
    }
  },
  addButton: {
    border: '1px solid rgba(118, 118, 125, 1)',
    height: '40px',
    width: '40px',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    }
  }
}

export default ModulesManager
